import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import type { ReportParameterType } from './../enums/ReportParameterType';

export class ReportParameter extends BaseObj {
    public ID: number;
    public __id: number;

    public DynamicData: string;
    public __dynamicdata: string;

    public Name: string | null;
    public __name: string | null;

    public Parameter: string | null;
    public __parameter: string | null;

    public ReportID: number;
    public __reportid: number;

    public Sortorder: number | null;
    public __sortorder: number | null;

    public TranslationKey: string | null;
    public __translationkey: string | null;

    public TranslationKeyTooltip: string | null;
    public __translationkeytooltip: string | null;

    public Type: ReportParameterType;
    public __type: ReportParameterType;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        DynamicData?: string | null;
        EntityState?: EntityState;
        Name?: string | null;
        Parameter?: string | null;
        ReportID?: number;
        Sortorder?: number | null;
        TranslationKey?: string | null;
        TranslationKeyTooltip?: string | null;
        Type?: ReportParameterType;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<ReportParameter>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('ReportParameter tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.DynamicData = values.DynamicData ?? '';
        this.__dynamicdata = values.DynamicData ?? '';
        Object.defineProperty(this, 'DynamicData', {
            get(): string {
                return this.__dynamicdata;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('DynamicData', this.__dynamicdata, value);
                this.__dynamicdata = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? null;
        this.__name = values.Name ?? null;
        Object.defineProperty(this, 'Name', {
            get(): string | null {
                return this.__name;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Parameter = values.Parameter ?? null;
        this.__parameter = values.Parameter ?? null;
        Object.defineProperty(this, 'Parameter', {
            get(): string | null {
                return this.__parameter;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Parameter', this.__parameter, value);
                this.__parameter = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ReportID = values.ReportID ?? 0;
        this.__reportid = values.ReportID ?? 0;
        Object.defineProperty(this, 'ReportID', {
            get(): number {
                return this.__reportid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ReportID', this.__reportid, value);
                this.__reportid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Sortorder = values.Sortorder ?? null;
        this.__sortorder = values.Sortorder ?? null;
        Object.defineProperty(this, 'Sortorder', {
            get(): number | null {
                return this.__sortorder;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Sortorder', this.__sortorder, value);
                this.__sortorder = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TranslationKey = values.TranslationKey ?? null;
        this.__translationkey = values.TranslationKey ?? null;
        Object.defineProperty(this, 'TranslationKey', {
            get(): string | null {
                return this.__translationkey;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('TranslationKey', this.__translationkey, value);
                this.__translationkey = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TranslationKeyTooltip = values.TranslationKeyTooltip ?? null;
        this.__translationkeytooltip = values.TranslationKeyTooltip ?? null;
        Object.defineProperty(this, 'TranslationKeyTooltip', {
            get(): string | null {
                return this.__translationkeytooltip;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('TranslationKeyTooltip', this.__translationkeytooltip, value);
                this.__translationkeytooltip = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Type = values.Type ?? 'Text';
        this.__type = values.Type ?? 'Text';
        Object.defineProperty(this, 'Type', {
            get(): ReportParameterType {
                return this.__type;
            },
            set(value: ReportParameterType) {
                this.__entityStateHandler.propertyChanged('Type', this.__type, value);
                this.__type = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"DynamicData": ${JSON.stringify(this.DynamicData)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"Parameter": ${JSON.stringify(this.Parameter)}` +
            ',' +
            `"ReportID": ${JSON.stringify(this.ReportID)}` +
            ',' +
            `"Sortorder": ${JSON.stringify(this.Sortorder)}` +
            ',' +
            `"TranslationKey": ${JSON.stringify(this.TranslationKey)}` +
            ',' +
            `"TranslationKeyTooltip": ${JSON.stringify(this.TranslationKeyTooltip)}` +
            ',' +
            `"Type": ${this.Type == 'Text' ? 1 : this.Type == 'Number' ? 2 : this.Type == 'DynamicDropdown' ? 3 : this.Type == 'StationRoleDropdown' ? 4 : this.Type == 'DynamicQueryDropdown' ? 5 : this.Type == 'StationRoleWithAllFinishNullDropdown' ? 6 : this.Type == 'File' ? 7 : this.Type == 'CategoriesMultiSelect' ? 8 : this.Type == 'Hidden' ? 9 : 1}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of ReportParameter from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        DynamicData?: string | null;
        Name?: string | null;
        Parameter?: string | null;
        ReportID?: number;
        Sortorder?: number | null;
        TranslationKey?: string | null;
        TranslationKeyTooltip?: string | null;
        Type?: ReportParameterType;
        EntityState?: 'Added' | 'Unchanged';
    }): ReportParameter {
        return new ReportParameter(values);
    }

    public static _FromServerJson(json: any): ReportParameter {
        return new ReportParameter({
            ID: json.ID,
            DynamicData: json.DynamicData ?? '',
            Name: json.Name ?? '',
            Parameter: json.Parameter ?? '',
            ReportID: json.ReportID,
            Sortorder: json.Sortorder ?? null,
            TranslationKey: json.TranslationKey ?? '',
            TranslationKeyTooltip: json.TranslationKeyTooltip ?? '',
            Type:
                json.Type == 1
                    ? 'Text'
                    : json.Type == 2
                      ? 'Number'
                      : json.Type == 3
                        ? 'DynamicDropdown'
                        : json.Type == 4
                          ? 'StationRoleDropdown'
                          : json.Type == 5
                            ? 'DynamicQueryDropdown'
                            : json.Type == 6
                              ? 'StationRoleWithAllFinishNullDropdown'
                              : json.Type == 7
                                ? 'File'
                                : json.Type == 8
                                  ? 'CategoriesMultiSelect'
                                  : json.Type == 9
                                    ? 'Hidden'
                                    : 'Text',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of ReportParameter from another instance of ReportParameter.
     * @param other The ReportParameter you want to clone
     * @returns A cloned ReportParameter
     */
    public static FromOther(other: ReportParameter): ReportParameter {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return ReportParameter._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ReportParameter.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'DynamicData'
            | 'Name'
            | 'Parameter'
            | 'ReportID'
            | 'Sortorder'
            | 'TranslationKey'
            | 'TranslationKeyTooltip'
            | 'Type'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'ReportParameter';
}
